import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, photoUrl } from "@/lib/api";
import SheetThumb from "@/components/SheetThumb";
import { useAuth } from "@/contexts/AuthContext";
import {
  WORKFLOW_TYPES, PIN_CATEGORY,
  formatDateShort, formatRelative, ROLE_LABEL,
} from "@/lib/utils";
import { StatusBadge } from "@/components/Badges";
import {
  ArrowLeft, Send, CheckCircle2, FileText, MapPin, Camera, History,
  User as UserIcon, ChevronRight, Link as LinkIcon, Sparkle,
  AlertTriangle, ShieldAlert,
} from "lucide-react";

const OVERRIDE_ROLES = ["admin", "project_manager", "owner"];

export default function WorkflowDetail() {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [machine, setMachine] = useState(null);
  const [comment, setComment] = useState("");
  const [isAnswer, setIsAnswer] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [transitionError, setTransitionError] = useState(null);
  const fileInput = useRef();

  const load = useCallback(async () => {
    if (!workflowId) return;
    const [{ data: d }, { data: u }] = await Promise.all([
      api.get(`/workflows/${workflowId}`),
      api.get("/users"),
    ]);
    setData(d);
    setUsers(u);
    try {
      const { data: m } = await api.get(`/workflows/state_machine/${d.workflow.type}`);
      setMachine(m);
    } catch {}
  }, [workflowId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 font-mono text-xs text-[var(--fop-text-3)]">Loading workflow…</div>;

  const { workflow: w, comments, history, sheet, pin, project } = data;
  const meta = WORKFLOW_TYPES[w.type] || {};
  const author = (cid) => users.find((u) => u.id === cid);
  const isOverdue = w.due_date && new Date(w.due_date) < new Date() && !(machine?.terminal || []).includes(w.status);
  const canOverride = OVERRIDE_ROLES.includes(user?.role);
  const allowedNext = (machine?.transitions?.[w.status]) || [];
  const statusLabel = (s) => machine?.labels?.[s] || s.replace(/_/g, " ");

  const update = async (patch) => {
    setTransitionError(null);
    try {
      await api.patch(`/workflows/${w.id}`, patch);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 422 && d?.allowed_next) {
        setTransitionError(d);
      } else {
        setTransitionError({ error: typeof d === "string" ? d : "Update failed" });
      }
    }
  };

  const submitOverride = async () => {
    if (!overrideTarget || overrideReason.trim().length < 5) return;
    try {
      await api.patch(
        `/workflows/${w.id}`,
        { status: overrideTarget },
        { params: { override: true, reason: overrideReason.trim() } },
      );
      setOverrideMode(false);
      setOverrideTarget("");
      setOverrideReason("");
      setTransitionError(null);
      await load();
    } catch (e) {
      setTransitionError({ error: e?.response?.data?.detail || "Override failed" });
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/workflows/${w.id}/comments`, { text: comment, is_answer: isAnswer });
    setComment("");
    setIsAnswer(false);
    await load();
  };
  const uploadPhoto = async (file) => {
    const form = new FormData();
    form.append("file", file);
    await api.post(`/workflows/${w.id}/photos`, form, { headers: { "Content-Type": "multipart/form-data" } });
    await load();
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="workflow-page">
      <Link to={project ? `/project/${project.id}` : "/portfolio"} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)] mb-3">
        <ArrowLeft size={12} /> {project ? project.name : "Back"}
      </Link>

      <div className="fop-surface-2 p-6 rounded-lg relative overflow-hidden mb-4" style={{ borderLeft: `3px solid ${meta.color}` }}>
        <div className="flex flex-wrap items-start gap-6">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: meta.color }}>
              {meta.label}
            </div>
            <div className="font-mono text-2xl font-bold text-[var(--fop-text)] mt-1 mb-1.5">{w.number}</div>
            <h1 className="font-display text-2xl text-[var(--fop-text)] leading-tight">{w.title}</h1>
            {w.description && (
              <p className="text-sm text-[var(--fop-text-2)] mt-2 max-w-3xl leading-relaxed">{w.description}</p>
            )}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <StatusBadge status={w.status} />
              {w.outcome && (
                <span className="pill" style={{ color: w.outcome === "pass" ? "var(--st-corrected)" : "var(--st-escalated)" }}>
                  {w.outcome === "pass" ? <CheckCircle2 size={10} /> : null} {w.outcome.toUpperCase()}
                </span>
              )}
              {isOverdue && (
                <span className="pill text-[var(--st-escalated)]" data-testid="workflow-overdue-badge">
                  <AlertTriangle size={10} /> OVERDUE
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 min-w-[280px]">
            <Field label="Status">
              <select
                value={w.status}
                onChange={(e) => update({ status: e.target.value })}
                className="text-xs py-1.5"
                data-testid="workflow-status-select"
              >
                <option value={w.status}>{statusLabel(w.status)}</option>
                {allowedNext.map((s) => (
                  <option key={s} value={s}>→ {statusLabel(s)}</option>
                ))}
              </select>
            </Field>
            <Field label="Assignee">
              <select value={w.assigned_to || ""} onChange={(e) => update({ assigned_to: e.target.value || null })} className="text-xs py-1.5" data-testid="workflow-assignee-select">
                <option value="">Unassigned</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Due"><div className="text-xs font-mono text-[var(--fop-text-2)]">{formatDateShort(w.due_date)}</div></Field>
            <Field label="Created"><div className="text-xs font-mono text-[var(--fop-text-2)]">{formatDateShort(w.created_at)}</div></Field>
            {w.type === "inspection" && (
              <>
                <Field label="Outcome">
                  <select value={w.outcome || ""} onChange={(e) => update({ outcome: e.target.value || null })} className="text-xs py-1.5" data-testid="workflow-outcome-select">
                    <option value="">—</option>
                    <option value="pass">Pass</option>
                    <option value="fail">Fail</option>
                    <option value="na">N/A</option>
                  </select>
                </Field>
              </>
            )}
          </div>
        </div>

        {/* Transition error / allowed-next callout */}
        {transitionError && (
          <div className="mt-4 p-3 rounded border border-[var(--st-escalated)]/40 bg-[var(--st-escalated)]/5 text-xs" data-testid="workflow-transition-error">
            <div className="text-[var(--st-escalated)] font-medium flex items-center gap-1.5">
              <ShieldAlert size={12} /> {transitionError.error}
            </div>
            {transitionError.allowed_next && (
              <div className="text-[var(--fop-text-3)] mt-1.5">
                Allowed next: {transitionError.allowed_next.map((s) => statusLabel(s)).join(" · ") || "—"}
              </div>
            )}
          </div>
        )}

        {/* Admin/PM override panel */}
        {canOverride && (
          <div className="mt-4 pt-4 border-t border-[var(--fop-border)]" data-testid="workflow-override-panel">
            {!overrideMode ? (
              <button
                onClick={() => setOverrideMode(true)}
                className="btn btn-ghost text-xs"
                data-testid="workflow-override-open"
              >
                <ShieldAlert size={12} /> Override status
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={overrideTarget}
                  onChange={(e) => setOverrideTarget(e.target.value)}
                  className="text-xs py-1.5"
                  data-testid="workflow-override-target"
                >
                  <option value="">Choose target state…</option>
                  {(machine?.states || [])
                    .filter((s) => s !== w.status)
                    .map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                </select>
                <input
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Reason (min 5 chars, audited)"
                  className="text-xs py-1.5 flex-1 min-w-[260px]"
                  data-testid="workflow-override-reason"
                />
                <button
                  onClick={submitOverride}
                  disabled={!overrideTarget || overrideReason.trim().length < 5}
                  className="btn btn-primary text-xs"
                  data-testid="workflow-override-submit"
                >
                  Submit override
                </button>
                <button
                  onClick={() => { setOverrideMode(false); setOverrideTarget(""); setOverrideReason(""); }}
                  className="btn btn-ghost text-xs"
                  data-testid="workflow-override-cancel"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-4">
          {/* Linked sheet — sheet-first hero block. Real thumbnail + pin overlay. */}
          <Panel title="Linked Plan" icon={FileText} testid="workflow-linked-plan-hero">
            {sheet ? (
              <button
                onClick={() => navigate(`/sheet/${sheet.id}`)}
                className="w-full flex items-center gap-4 px-4 py-4 rounded-md border border-[var(--fop-border)] hover:border-[var(--fop-primary)] hover:bg-[var(--fop-surface)] transition-colors group text-left"
                data-testid="workflow-linked-sheet"
              >
                <SheetThumb
                  sheetId={sheet.id}
                  size="lg"
                  pin={pin ? { x: pin.x, y: pin.y, color: PIN_CATEGORY[pin.category]?.color } : null}
                  data-testid="workflow-linked-sheet-thumb"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-0.5">
                    Sheet · REV {sheet.revision}
                  </div>
                  <div className="font-display text-xl font-bold text-[var(--fop-text)] group-hover:text-[var(--fop-primary)] leading-tight">
                    {sheet.sheet_number}
                  </div>
                  <div className="text-sm text-[var(--fop-text-2)] truncate mt-0.5">
                    {sheet.sheet_title}
                  </div>
                  {pin && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                      <MapPin size={10} style={{ color: PIN_CATEGORY[pin.category]?.color }} />
                      Pin · page {pin.page || 1} · {Math.round((pin.x || 0) * 100)}% / {Math.round((pin.y || 0) * 100)}%
                    </div>
                  )}
                </div>
                <ChevronRight size={14} className="text-[var(--fop-text-3)] shrink-0 group-hover:text-[var(--fop-primary)]" />
              </button>
            ) : (
              <div className="text-xs text-[var(--fop-text-3)] px-3 py-3 border border-dashed border-[var(--fop-border)] rounded-md">
                This record is not linked to a sheet.
              </div>
            )}
          </Panel>

          {/* Photos */}
          <Panel title={`Photos · ${w.photos?.length || 0}`} icon={Camera}>
            <div className="grid grid-cols-4 gap-2">
              {(w.photos || []).map((p, i) => (
                <a key={i} href={photoUrl(p)} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded border border-[var(--fop-border)]">
                  <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
                </a>
              ))}
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="aspect-square rounded border-2 border-dashed border-[var(--fop-border-2)] hover:border-[var(--fop-primary)] flex flex-col items-center justify-center gap-1 text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] transition-colors"
                data-testid="workflow-add-photo"
              >
                <Camera size={20} />
                <span className="text-[10px] uppercase font-mono tracking-wider">Add</span>
              </button>
              <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
            </div>
          </Panel>

          {/* Conversation */}
          <Panel title={`Conversation · ${comments.length}`}>
            {comments.length === 0 ? (
              <div className="text-sm text-[var(--fop-text-3)]">No comments yet.</div>
            ) : (
              <ul className="space-y-3">
                {comments.map((c) => {
                  const u = author(c.author_id);
                  return (
                    <li key={c.id} className={`p-3 rounded-md border ${c.is_answer ? "border-[rgba(52,211,153,0.4)] bg-[rgba(52,211,153,0.06)]" : "border-[var(--fop-border)] bg-[var(--fop-surface)]"}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-full bg-[var(--fop-surface-3)] flex items-center justify-center">
                          <UserIcon size={11} className="text-[var(--fop-text-3)]" />
                        </div>
                        <span className="text-sm text-[var(--fop-text)] font-medium">{u?.name || "Unknown"}</span>
                        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{u && ROLE_LABEL[u.role]}</span>
                        {c.is_answer && (
                          <span className="pill" style={{ color: "var(--st-corrected)", background: "rgba(52,211,153,0.12)", borderColor: "rgba(52,211,153,0.4)" }}>
                            <Sparkle size={10} /> ANSWER
                          </span>
                        )}
                        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] ml-auto">{formatRelative(c.created_at)}</span>
                      </div>
                      <p className="text-sm text-[var(--fop-text)] leading-snug whitespace-pre-wrap">{c.text}</p>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="mt-4 fop-surface p-3 rounded-md border border-[var(--fop-border-2)]">
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={w.type === "rfi" ? "Add a response or mark as the answer…" : "Add a comment…"}
                data-testid="workflow-comment-input"
              />
              <div className="flex items-center gap-3 mt-2">
                {w.type === "rfi" && (
                  <label className="flex items-center gap-2 text-xs text-[var(--fop-text-2)] cursor-pointer">
                    <input type="checkbox" checked={isAnswer} onChange={(e) => setIsAnswer(e.target.checked)} className="w-auto" data-testid="workflow-mark-answer" />
                    Mark as answer
                  </label>
                )}
                <button onClick={addComment} disabled={!comment.trim()} className="btn btn-primary ml-auto" data-testid="workflow-comment-submit">
                  <Send size={13} /> {isAnswer ? "Post answer" : "Comment"}
                </button>
              </div>
            </div>
          </Panel>

          {/* History */}
          <Panel title="Audit history" icon={History}>
            {history.length === 0 ? (
              <div className="text-sm text-[var(--fop-text-3)]">No changes recorded yet.</div>
            ) : (
              <ul className="space-y-0.5">
                {history.map((h) => (
                  <li key={h.id} className="flex gap-3 text-xs py-1.5 border-b border-[var(--fop-border)]">
                    <span className="font-mono text-[var(--fop-text-3)] w-20 shrink-0 uppercase tracking-wider">{formatRelative(h.created_at)}</span>
                    <span className="text-[var(--fop-text)]">
                      <span className="text-[var(--fop-text-3)]">{h.field}:</span>{" "}
                      {String(h.old_value || "—")} → <span style={{ color: meta.color }}>{String(h.new_value)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        {/* Linked items sidebar — Linked Plan moved to hero block above */}
        <div className="space-y-3">
          {project && (
            <Panel title="Project">
              <Link
                to={`/project/${project.id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-[var(--fop-border)] hover:border-[var(--fop-primary)] transition-colors group"
                data-testid="workflow-linked-project"
              >
                <div className="w-1 self-stretch rounded-full" style={{ background: project.cover_color || "var(--fop-primary)" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--fop-text)] font-medium truncate group-hover:text-[var(--fop-primary)]">{project.name}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{project.number}</div>
                </div>
              </Link>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1">{label}</div>
      {children}
    </div>
  );
}
function Panel({ title, icon: Icon, testid, children }) {
  return (
    <div className="fop-surface-2 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={12} className="text-[var(--fop-primary)]" />}
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">{title}</div>
      </div>
      {children}
    </div>
  );
}
